import React from 'react';
import propTypes from 'prop-types';
import { SBE_STATES } from 'helpers/appointments/ffmCertification';
import { getStateNameFromStateCode } from 'helpers/states';
import { cn } from 'lib/utils';
import SbeGridHeader from './SbeGridHeader';
import StatusGridHeader from './StatusGridHeader';

// neutral chip body + bottom accent bar per oep status. keeps the "all
// available states" grid visually calm while still flagging state-level
// changes at a glance. all variants share the same total border widths so
// chip heights stay uniform.
const STATUS_STYLES = {
  Added:
    'bg-gray-100 text-gray-800 border border-gray-200 border-b-4 border-b-green-400',
  Updated:
    'bg-gray-100 text-gray-800 border border-gray-200 border-b-4 border-b-amber-400',
  Removed:
    'bg-gray-100 text-gray-800 border border-gray-200 border-b-4 border-b-red-400'
};
const NEUTRAL_STATUS_STYLE =
  'bg-gray-100 text-gray-700 border border-gray-200 border-b-4 border-b-transparent';

// human-readable status labels shown in the chip tooltip. counties detail
// still lives in the "Carrier Updates" section above, so here we only tell
// the agent WHICH KIND of change the state has, not what changed inside.
const STATUS_TOOLTIP_LABELS = {
  Added: 'New state',
  Updated: 'Updated state',
  Removed: 'Removed state'
};

/**
 * grid of state chips used by carrier resources and oep updates. defaults to
 * the shared SBE/marketplace color scheme with the full state name inside the
 * chip. opt into oep behavior via:
 *  - `abbreviated` to show 2-letter codes instead of full names,
 *  - `stateStatusMap` to override chip colors with the derived oep status,
 *  - the native tooltip always shows the full state name and (when present)
 *    the derived status.
 * @param states - array of 2-letter state codes to render
 * @param carrierName - heading shown above the grid
 * @param ignoreSbeAndFfmLabels - hides the SBE/Marketplace legend and falls
 *   back to `bg-primary-darkened` when no status map is provided
 * @param abbreviated - render 2-letter codes instead of full names
 * @param stateStatusMap - optional record of `stateCode -> status` used to
 *   tint each chip; states with no entry render as neutral
 */
export default function CarrierDetailStates({
  states,
  carrierName,
  ignoreSbeAndFfmLabels,
  abbreviated = false,
  stateStatusMap = null
}) {
  if (!states || states.length === 0) return null;

  const useStatusColors = stateStatusMap !== null;

  return (
    <section>
      {useStatusColors ? (
        <StatusGridHeader
          title={carrierName}
          states={states}
          stateStatusMap={stateStatusMap}
        />
      ) : (
        <SbeGridHeader
          title={carrierName}
          showLegend={!ignoreSbeAndFfmLabels}
        />
      )}
      <div
        className={cn(
          'grid gap-x-2 gap-y-1.5 mt-2',
          abbreviated ? 'state-code-grid' : 'grid-cols-4 lg:grid-cols-6'
        )}
      >
        {states.map(code => renderChip({
          code,
          abbreviated,
          useStatusColors,
          status: stateStatusMap ? stateStatusMap[code] : null,
          ignoreSbeAndFfmLabels
        }))}
      </div>
    </section>
  );
}

// picks the chip style + tooltip content for a single state code. kept outside
// the component body to keep the render tree flat (no nested ternaries).
function renderChip({
  code,
  abbreviated,
  useStatusColors,
  status,
  ignoreSbeAndFfmLabels
}) {
  const fullName = getStateNameFromStateCode(code) || code;
  const label = abbreviated ? code : fullName;
  const tooltip = resolveTooltip({ fullName, useStatusColors, status });

  const className = cn(
    'px-1.5 py-0.5 lg:px-2 lg:py-1 rounded-md text-xs lg:text-sm font-semibold select-none text-center cursor-default transition-colors',
    resolveChipTone({
      useStatusColors,
      status,
      code,
      ignoreSbeAndFfmLabels
    })
  );

  return (
    <span key={code} title={tooltip} className={className}>
      {label}
    </span>
  );
}

// picks the tooltip string. in status mode we prefer the human-readable label
// ("New state", "Updated state", "Removed state") over the raw status token.
function resolveTooltip({ fullName, useStatusColors, status }) {
  if (useStatusColors && status) {
    return `${fullName} — ${STATUS_TOOLTIP_LABELS[status] || status}`;
  }
  return fullName;
}

// resolves the tailwind classes for a single state chip based on which color
// scheme is active (status-tinted, SBE/marketplace, or the neutral fallback).
function resolveChipTone({
  useStatusColors,
  status,
  code,
  ignoreSbeAndFfmLabels
}) {
  if (useStatusColors) {
    return status ? STATUS_STYLES[status] : NEUTRAL_STATUS_STYLE;
  }
  if (ignoreSbeAndFfmLabels) {
    return 'border border-transparent text-white bg-primary-darkened';
  }
  const isSBE = SBE_STATES.includes(code);
  return isSBE
    ? 'border border-transparent text-white bg-[#9ad7fa]'
    : 'border border-transparent text-white bg-[#0056a3]';
}

CarrierDetailStates.propTypes = {
  states: propTypes.arrayOf(propTypes.string),
  carrierName: propTypes.string,
  ignoreSbeAndFfmLabels: propTypes.bool,
  abbreviated: propTypes.bool,
  stateStatusMap: propTypes.object
};
